import { FC } from "react";

import { useTranslation } from "@/core/i18n";
import { Text } from "atoms/Text/Text";
import { useUser } from "modules/user/useUser/useUser";

import { normalizePersona, replacePersonaPlaceholders } from "../adonis.helpers";
import { useAdonisChat } from "../AdonisChatProvider/AdonisChatProvider";
import adonisHeaderBg from "../assets/adonis_header_bg.webp";
import ariaHeaderBg from "../assets/aria_header_bg.webp";

interface AdvisorShopCategoryHeaderText {
  title: string;
  subtitle: string;
}

export interface AdvisorShopCategoryHeaderProps {
  headerText?: AdvisorShopCategoryHeaderText;
  className?: string;
}

const VARIANT_BACKGROUNDS: Record<string, string> = {
  Adonis: adonisHeaderBg,
  Aria: ariaHeaderBg,
};

export const AdvisorShopCategoryHeader: FC<AdvisorShopCategoryHeaderProps> = ({
  headerText,
  className = "",
}) => {
  const { t } = useTranslation();
  const { persona } = useAdonisChat();
  // Normalize persona to ensure we always have valid values (defaults to Adonis)
  const normalizedPersona = normalizePersona(persona);
  const { user } = useUser();

  const username = user?.firstName ?? "";

  const defaultText: AdvisorShopCategoryHeaderText = {
    title: t("advisorShopCategoryHeader.title"),
    subtitle: t("advisorShopCategoryHeader.subtitle"),
  };

  const mergedText: AdvisorShopCategoryHeaderText = {
    title: headerText?.title ?? defaultText.title,
    subtitle: headerText?.subtitle ?? defaultText.subtitle,
  };

  const text: AdvisorShopCategoryHeaderText = {
    title: replacePersonaPlaceholders(
      mergedText.title.replace("{username}", username),
      normalizedPersona,
    ),
    subtitle: replacePersonaPlaceholders(mergedText.subtitle, normalizedPersona),
  };

  const backgroundImage = VARIANT_BACKGROUNDS[normalizedPersona.name] || adonisHeaderBg;

  return (
    <div
      className={`flex h-[200px] w-full items-center overflow-hidden desk:h-[220px] ${className}`}
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* The same column as the shop tabs above and the grid below, at every width. */}
      <div className="mx-auto w-full max-w-6xl px-6 desk:px-8">
        {/* Three-fifths of a phone is a measure; three-fifths of a desktop window
            is a run-on line, so the copy stops at the width it was set for. */}
        <div className="flex w-3/5 flex-col desk:max-w-[480px]">
          <Text component="h1" className="mb-1 font-serif text-h1 font-medium text-primary">
            {text.title}
          </Text>
          <Text component="p" className="text-body2">
            {text.subtitle}
          </Text>
        </div>
      </div>
    </div>
  );
};
